import { pick } from '@shared/helpers/object.helpers';
import { EnergyProductId } from '../energy/energy.model';
import { IElement } from '@shared/models/project.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';

/**
 * The mass of the air handling unit in kg per m³/s of airflow.
 * Value taken from: https://docs.google.com/spreadsheets/d/14sK0tG090cT9Mo2-cFtbftd9Nwg0HnicHwgm-vBduwQ/edit?usp=sharing
 */
export const AHU_MASS_PER_AIRFLOW = 0.00075;

export const AHU_ENERGY_UNIT = 'kWh';

/**
 * The default specific fan power of the air handling unit in kW/m³/s.
 */
export const AHU_DEFAULT_SPECIFIC_FAN_POWER = 1.8;

/**
 * How much of the exhaust air heat is recovered by the air handling unit.
 */
export const AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY = 0.8;

export enum AHU_VARIABLE_NAME {
  FAN_ENERGY_CONSUMPTION = 'annual_energy_consumption_fan',
  POST_HEATING_ENERGY_CONSUMPTION = 'annual_energy_consumption_post_heating',
  TOTAL_ENERGY_CONSUMPTION = 'annual_energy_consumption_total',
}

export const ACTIVITY_VENTILATION_HOURS_VARIABLE = 'activity_ventilation_hours';

export const LOCATION_COOLING_HOURS_VARIABLE = 'location_cooling_hours';

export const POST_HEATING_ENERGY_SOURCES = pick(
  EnergyProductId,
  'DistrictHeating',
  'Electricity',
);

export type AirHandlingUnitElement = IElement & {
  category_id: ElementCategoryID.AirHandlingUnit;
};

export enum AirHandlingUnitPropertyName {
  Products = 'products',

  /**
   * How much area is heated or cooled.
   */
  VentilationArea = 'ventilation_area',

  /**
   * The specific fan power of the air handling unit.
   * How much power in kW the fan consumes per m³/s of airflow.
   */
  SpecificFanPower = 'specific_fan_power',

  /**
   * The heat recovery efficiency of the air handling unit.
   * How much heat recovery efficiency the air handling unit has.
   */
  HeatRecoveryEfficiency = 'heat_recovery_efficiency',

  SupplyAirFlowPerGFA = 'supply_air_flow_per_gfa',
  ExhaustAirFlowPerGFA = 'exhaust_air_flow_per_gfa',
  OperationHours = 'operation_hours',
  SupplyAirPostHeating = 'supply_air_post_heating',
  PostHeatingTemperature = 'post_heating_temperature',
  PostHeatingHours = 'post_heating_hours',
  SupplyAirPreCooling = 'supply_air_pre_cooling',
  PreCoolingTemperature = 'pre_cooling_temperature',
  PreCoolingHours = 'pre_cooling_hours',
  /**
   * The energy effect ratio of the air handling unit.
   * How many kWh of cooling is generated per kWh of electricity consumed (basically same as COP)
   */
  PreCoolingEffectRatio = 'pre_cooling_effect_ratio',

  /**
   * The airflow produced by the air handling unit in m³/s.
   */
  Airflow = 'airflow',

  /**
   * The energy source of the post heating.
   */
  PostHeatingEnergySource = 'post_heating_energy_source',
}
